import React, {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
} from "react"
import { Fab, makeStyles, Theme, Portal } from "@material-ui/core"
import { AddRounded as AddRoundedIcon } from "@material-ui/icons"
import { useEsriModules } from "../common/useEsriModules"
import { useMapView } from "../common/MapProvider"

import { FeatureFormModal } from "../feature-form/FeatureFormModal"
import { useData } from "../common/DataProvider"
import { applyEditsResult } from "../feature-form/FeatureForm"
import { useHistory, useParams } from "react-router-dom"
import { useDraggableGraphic } from "../common/useDraggableGraphic"
import mapPin from "./map-pin.png"

/** AddFeatureButton component props */
interface AddFeatureButtonProps {}

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles<Theme, { adding: boolean }>(theme => ({
  fab: {
    position: "absolute",
    right: 0,
    bottom: theme.spacing(8),
    zIndex: 99,
    transform: ({ adding }) => `translateX(50%) rotate(${adding ? 45 : 0}deg)`,
    transition: theme.transitions.create("transform"),
    border: "1px solid white",
    width: 56,
    height: 56,
  },
}))

export const NEW_GRAPHIC_OBJECTID = "NEW_GRAPHIC"

export const AddFeatureButton: FunctionComponent<AddFeatureButtonProps> = () => {
  /** Track whether the user is adding a feature */
  const [adding, setAdding] = useState(false)

  /** Grab Esri stuff we need */
  const [Graphic] = useEsriModules<[typeof __esri.Graphic]>(["esri/Graphic"])

  const { view } = useMapView()
  const [graphic, setGraphic] = useState<__esri.Graphic | undefined>()
  useEffect(() => {
    if (adding && view && Graphic) {
      const graphic = new Graphic({
        symbol: {
          // @ts-ignore
          type: "picture-marker",
          url: mapPin,
          width: 36,
          height: 36,
        },
        attributes: {
          OBJECTID: "NEW_GRAPHIC",
        },
      })

      // when the view loads
      view.when().then(() => {
        graphic.geometry = view.center.clone()
        view.graphics.add(graphic)
        setGraphic(graphic)
      })

      // Remove the graphic on cleanup (i.e. when adding changes to false)
      return () => {
        view.graphics.remove(graphic)
        setGraphic(undefined)
      }
    }
  }, [adding, view, Graphic])

  /** Add the draggable marker */
  useDraggableGraphic(graphic)

  /** Handle closing the modal */
  const handleClose = useCallback(() => {
    if (graphic) {
      view?.graphics.remove(graphic)
      setAdding(false)
      setGraphic(undefined)
    }
  }, [graphic, view])

  /** Get scoped CSS classes */
  const classes = useStyles({ adding })

  /** Handle success */
  const {
    activeFeatureLayer: [featureLayer],
  } = useData()
  const history = useHistory()
  const handleSuccess = (results: applyEditsResult) => {
    handleClose()
    featureLayer &&
      history.push(
        `/layers/${featureLayer.id}/${results.addResults[0].objectId}`
      )
  }

  /** Handle button click */
  const handleClick = () => {
    setAdding(adding => {
      if (!adding && featureLayer) {
        history.push(`/layers/${featureLayer.id}`)
      }

      return !adding
    })
  }

  /** Close adding when a feature layer is being edited */
  const { featureId } = useParams<{ featureId?: string }>()
  useEffect(() => {
    if (featureId) {
      handleClose()
    }
  }, [featureId, handleClose])

  return (
    <>
      <Fab
        size="large"
        className={classes.fab}
        color="secondary"
        onClick={handleClick}
      >
        <AddRoundedIcon />
      </Fab>
      <Portal container={document.getElementById("map-portal-outlet")}>
        <FeatureFormModal
          featureLayer={featureLayer}
          feature={graphic}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      </Portal>
    </>
  )
}
